import {
    type BnbSettingsActionsType,
    BNB_SETTINGS_ENABLE_BUY,
    BNB_SETTINGS_ENABLE_LEASE,
    BNB_SETTINGS_ENABLE_RESERVE,
    BNB_SETTINGS_INIT,
    BNB_SETTINGS_MODEL_HAS_FILTERS,
    BNB_SETTINGS_SET_PAGE_MONTHLY_RATE,
} from "../actions/BnbSettingsActions";
import { type BuildAndBuyLabelType, buildAndBuyLabels } from "../../utils/labels";
import type { AemEcoLabelSettingsType, ServiceType } from "../../../../../common-deprecated/types/SettingsType";
import type { FinanceOptionType } from "../../../../../common-deprecated/types/CommonTypes";
import type { CTAConfigType } from "../../../../../common-deprecated/server/data/FormatBnbCtas";
import type { NavStep } from "../../../shared-logic/utils/Navigation";
import type { ExternalPriceLocationType } from "../../../../../common-deprecated/utils/taxLocations";
import {
    type FinanceSettingsType,
    type InsuranceSettingsType,
    initialFinanceSettingsData,
    initialInsuranceSettingsData,
} from "../../../../shared-logic/types/CommonSettingsType";

// TODO Consider some kind of broader structure like bnbSettings.financeSettings/bnbSettings.basketSettings/...?
// Please add additional block comments when required to maintain an overview of setting types and document what each setting does
export type BnbSettingsType = {
    // ----------------------------------------------------------------------
    // General
    // ----------------------------------------------------------------------
    initialized: boolean;
    labels: Record<BuildAndBuyLabelType, string>;
    loadSaveServer: string; // CCSave endpoint.
    modelFilterUrl: string; // This used to be orCarExplorer but renamed for more clarity.
    services: ServiceType[]; // Contains info for services step.
    enableIntegratedTradeIn: boolean; // When enabled trade-in aem also needs to support the current country.
    ctaConfigs?: CTAConfigType; // Settings used for the CTA's, More explanation in typing (AEM only)
    disableBuildAccessories: boolean; // TODO Can someone add a comment about what this does? I cannot figure it out based on code usage (seemingly only for hideWheelPrices?)
    aemEcoLabel?: AemEcoLabelSettingsType; // Setting used to reference the corresponding eco image (based on the energy efficiency class) in the summary of the Build
    preselectedEquipmentFilterId: string; // Determines whether or not to show the filter page for preselected Equipment items
    hideSaveToYourAccount: boolean; // Setting used to disable the save to your account button on build summary.
    hideTyCode: boolean; // Used to hide the tycode on Summary & PDF
    enableBuildSPA: boolean; // Will enable SPA like behaviour from customize onwards
    enableBuy: boolean; // Used to enable the buy step. Note that this setting does not only rely on the i18n value, but also on a car state requirement. See BuyReserveLogic.ts
    enableReserve: boolean; // Used to enable the reserve step. Note that this setting does not only rely on the i18n value, but also on a car state requirement. See BuyReserveLogic.ts
    allowedCheckoutFinanceProduct: string; // Contains the productNames in which you can go to buy checkout flow with finance
    allowedCheckoutReserveProduct: string; // Contains the productNames in which you can go to checkout with reservation
    modelHasActiveFilters: boolean; // Used to show/hide the modelFilters
    // STOP! Before adding settings here please consider if they match their subject. If it is no longer 'General' consider gathering the relevant subjects into their own block.

    // ----------------------------------------------------------------------
    // Leasing
    // ----------------------------------------------------------------------
    allowedCheckoutLeasingProduct: string; // Contains the productNames in which you can go to checkout with leasing
    enableLeasing: boolean; // Used to enable/disable leasing. Note that this setting does not only rely on the i18n value, but also on a car state requirement. See BuyReserveLogic.ts
    allowedTradeinProduct: string; // Contains the productNames in which tradein is enabled.

    // ----------------------------------------------------------------------
    // ETA
    // ----------------------------------------------------------------------
    showETA: boolean; // Setting to determine the showing of ETA
    summaryEtaMatchingType: string; // Determines which cars are returned for ETA, see https://t1-preview-wiki.toyota-europe.com/index.php/CarConfig_Settings#summaryEtaMatchingType
    urlStockCarsLandingPage: string; // Url to the stock cars landing page for the eta modal
    // ----------------------------------------------------------------------
    // Finance
    // ----------------------------------------------------------------------
    financeGateway: string; // Contains the finance endpoint that is configured in the .env file of the server, Used in the Finance Modal
    defaultFinanceOption: FinanceOptionType; // Contains the default finance option from settings, however the actual default is calculated in postFetch.
    loadFinanceServices: boolean; // Indicates if we need to try to fetch finance services (Finance info/Flexibility matrix/...) Mostly used server side.
    showPageMonthlyRate: boolean; // An additional showMonthlyRate setting depending on the step.
    showMonthlyRateCarConfig: boolean; // Another setting which allows hiding monthly rates. Kept the 'carconfig' name for readability.
    showMonthlyRateDisclaimerInline: boolean; // Show monthly disclaimers inline instead of in a modal.
    hideCarHeaderMonthlyRate: boolean; // Setting used to hide the monthly rate in the car header on the customize step
    hideSecondaryMonthlyPriceView: boolean; // Setting used to the monthly rate
    addPSLSToFinanceConfigurationObject: boolean; // Triggers PSLS logic during finance rate fetch.
    pslsGateway: string; // Contains the value from the environment variable PSLS_GATEWAY to be used in the front-end
    monthlyRateService: string; // Link pointing to the finance monthly rate service
    monthlyRateDisclaimerService: string; // Link pointing to the finance rate legal text service
    monthlyRateConfigurablesService: string; // Link pointing to the finance rate service used for configurables (colours, wheels, packs, upholsteries, etc,...)
    monthlyRateSelectedService: string; // Link pointing to the finance rate service used for configurables (colours, wheels, packs, upholsteries, etc,...) in the print/pdf
    showAccessoriesRate: boolean; // Setting which shows triggers the logic to show monthly rates on configurables
    toggleConfigurablesMonthlyRate: boolean; // If set to false, this makes sure that when toggling to monthly rates when showAccessoriesRate is true, the cash price remains
    sentFinanceStepToyotaCode: boolean; // When set to true will also sent the Toyota code as part of the payload when opening the finance calculator
    financeModuleURL: string; // Link pointing to the AMD JavaScript file used for the finance calculator | If the url contains the keyword 'compare' we activate pcp-pch logic/flow
    financeCalculatorEnvironment: string; // Used as parameter "environment:financeCalculatorEnvironment" at the configuration object which is sent to the finance frame, only for preview
    personalFinanceEnabled: boolean; // Setting only used in AEM Build considering there is not setting to configure this in the AEM flexibility matrix. This enables bidirectinal communication between the finance calculator and the finance rate.
    showBasketQuoteInfo: boolean; // Setting used to determine the quote info being shown
    disableFinanceSteps?: NavStep[]; // Setting used for disabling finance on certain steps of the build
    showMonthlyRatesFootnote: boolean;
    disableMonthlyPromoIcon: boolean; // Setting used to disable te promotions next to the monthly price
    showFinanceCalculatorAsPrimaryCTA: boolean; // Setting used to decide if the financeCalculator can be shown as a primary CTA when financeQuoteId is not filled in.
    showFinanceInsuranceInPage?: boolean; // Setting used to decide if the financeInsuranceCalculator should be rendered in page or in popup
    showMergedModelStep?: boolean; // Setting used to decide if grade-engine step should be render or original model step
    showPackExtras?: boolean; // Setting to hide the pack-extras from showing in the extra's section
    enableFinanceCompare: boolean; // Setting that gets auto-set when financeModuleUrl contains compare
    disableTradeInForFinanceProducts?: string[]; // Trade-in data will become incompatible for every finance product added. When incompatible, no trade-in data will be sent to any finance endpoints.
    // STOP! Before adding settings here please consider if they match their subject (See blockComments)

    // ----------------------------------------------------------------------
    // Insurance
    // ----------------------------------------------------------------------
    showInsuranceRateCarConfig: boolean; // Another setting which allows hiding insurance. Kept the 'carconfig' name for readability.
    requireDefaultInsurance: boolean; // If enabled, will try to retrieve a default insurance quote
    personalInsuranceEnabled: boolean; // Setting only used in AEM Build considering there is not setting to configure this in the AEM flexibility matrix. This enables bidirectinal communication between the insurance calculator and the insurance rate.
    addPSLSToInsuranceConfigurationObject: boolean; // Triggers PSLS logic during insurance rate fetch

    // ----------------------------------------------------------------------
    // External price calculation (TMEF)
    // ----------------------------------------------------------------------
    priceCalculationService: string; // Link to TMEF Fomula endpoint which returns the JavaScript function
    priceCalculationServiceAPI: string; // Link to TMEF Calculate endpoint
    enableWLTPPriceCalculation: boolean; // Enable price calculation which also requires the emission values to be WLTP certification.
    enablePriceServiceCalculation: boolean; // Enable/disable external price calculation, the difference between the the setting above is that it does not require the certification to be WLTP (used for Turkey's price calculation).
    showExternalPriceServiceTaxSection: boolean; // Determines where the tax is shown, above the price or beneath the price in a separate section, currently only Turkey has this configured to false since they use external calculation but don't show a tax.
    taxLocations: ExternalPriceLocationType[]; // Setting used for the tax and costs region selector for TFR
    taxFormats: { label: string; key: string; disclaimer?: string }[]; // setting to determine the format for taxes and costs TFR

    // ----------------------------------------------------------------------
    // Disclaimer settings
    // ----------------------------------------------------------------------
    collapsePromoDisclaimers: boolean; // Setting to make the disclaimers at the bottom of the build collapsible.
    collapseMobileDisclaimers: boolean; // Setting to make the disclaimers at the bottom of the build collapsible on mobile.
    hidePromotionDisclaimers: boolean; // Setting to not show the promotional disclaimers on the bottom
    showPrintDisclaimer: boolean; // Setting to show/hide the print disclaimer
    // ----------------------------------------------------------------------
    // Disclaimer settings
    // ----------------------------------------------------------------------
    ecoConsumptionCostService: string; // Setting to determine setting endpoint for the eco consumption costs section
} & FinanceSettingsType &
    InsuranceSettingsType;

export const initialStateBnbSettings: BnbSettingsType = {
    initialized: false,
    labels: { ...buildAndBuyLabels },
    loadSaveServer: "",
    loadFinanceServices: false,
    summaryEtaMatchingType: "",
    modelFilterUrl: "",
    modelHasActiveFilters: false,
    services: [],
    enableIntegratedTradeIn: false,
    financeGateway: "",
    defaultFinanceOption: "cash",
    showPageMonthlyRate: true,
    showMonthlyRateCarConfig: false,
    showInsuranceRateCarConfig: false,
    showMonthlyRateDisclaimerInline: false,
    hideCarHeaderMonthlyRate: false,
    hideSecondaryMonthlyPriceView: false,
    addPSLSToFinanceConfigurationObject: false,
    addPSLSToInsuranceConfigurationObject: false,
    pslsGateway: "",
    monthlyRateService: "",
    monthlyRateDisclaimerService: "",
    monthlyRateConfigurablesService: "",
    monthlyRateSelectedService: "",
    showAccessoriesRate: false,
    toggleConfigurablesMonthlyRate: true,
    sentFinanceStepToyotaCode: false,
    financeModuleURL: "",
    financeCalculatorEnvironment: "",
    requireDefaultInsurance: false,
    priceCalculationService: "",
    enableWLTPPriceCalculation: false,
    enablePriceServiceCalculation: false,
    showExternalPriceServiceTaxSection: false,
    preselectedEquipmentFilterId: "",
    personalFinanceEnabled: false,
    personalInsuranceEnabled: false,
    disableBuildAccessories: false,
    hideSaveToYourAccount: false,
    hideTyCode: false,
    showBasketQuoteInfo: false,
    showETA: false,
    ctaConfigs: undefined,
    disableFinanceSteps: [],
    showMonthlyRatesFootnote: false,
    disableMonthlyPromoIcon: false,
    enableBuildSPA: false,
    allowedCheckoutFinanceProduct: "",
    allowedCheckoutReserveProduct: "",
    allowedCheckoutLeasingProduct: "",
    enableLeasing: false,
    enableBuy: false,
    enableReserve: false,
    ecoConsumptionCostService: "",
    collapsePromoDisclaimers: false,
    collapseMobileDisclaimers: false,
    hidePromotionDisclaimers: false,
    showPrintDisclaimer: false,
    allowedTradeinProduct: "",
    urlStockCarsLandingPage: "",
    taxLocations: [],
    taxFormats: [],
    priceCalculationServiceAPI: "",
    showFinanceCalculatorAsPrimaryCTA: false,
    showFinanceInsuranceInPage: false,
    showPackExtras: true,
    showMergedModelStep: false,
    enableFinanceCompare: false,
    ...initialFinanceSettingsData,
    ...initialInsuranceSettingsData,
};

const BnbSettingsReducer = (
    state: BnbSettingsType = initialStateBnbSettings,
    action: BnbSettingsActionsType,
): BnbSettingsType => {
    switch (action.type) {
        case BNB_SETTINGS_INIT:
            return { ...state, ...action.initState, initialized: true };

        case BNB_SETTINGS_SET_PAGE_MONTHLY_RATE:
            return { ...state, showPageMonthlyRate: action.showPageMonthlyRate };

        case BNB_SETTINGS_ENABLE_BUY:
            return { ...state, enableBuy: action.enable };

        case BNB_SETTINGS_ENABLE_RESERVE:
            return { ...state, enableReserve: action.enable };

        case BNB_SETTINGS_ENABLE_LEASE:
            return { ...state, enableLeasing: action.enable };

        case BNB_SETTINGS_MODEL_HAS_FILTERS:
            return { ...state, modelHasActiveFilters: action.activeFilters };

        default:
            return state;
    }
};

export default BnbSettingsReducer;
